import { useCallback, useRef } from 'react';
import type { FileRejection } from 'react-dropzone';
import { noop } from 'lodash';

import { NOTIFICATION_LEVEL } from 'src/data/notifications';
import {
  type CustomerResource,
  enrichResourceFileInfo,
  getContentTypeTag,
  getResourceContentError,
  RESOURCE_FILE_UPLOAD_ERROR_CODE,
  type ResourceContentType,
} from 'src/data/performance';
import { parseError } from 'src/lib/common';
import { calculateFileMD5 } from 'src/lib/crypto';
import type { Callback } from 'src/lib/types';
// @ts-expect-error - useCustomerActions does not have types yet
import { useCustomerActions } from 'src/store/customer';
// @ts-expect-error - useNotificationActions does not have types yet
import { useNotificationActions } from 'src/store/notification';
// @ts-expect-error - useResourceActions does not have types yet
import { useResourceActions } from 'src/store/resource';

type ResultCallback = Callback<{ file?: File; resource?: CustomerResource; resourceId?: string }>;
type ErrorCallback = Callback<string | undefined>;
type UploadingChangeCallback = Callback<boolean>;

type HookReturnCallback = (acceptedFiles: File[], fileRejections?: FileRejection[]) => void;

/**
 * Process the selected file and check if we already have a resource for the same file
 *
 * @param resourceType - resource type
 * @param onResult - Callback to be called when the file is processed
 * @param onError - Callback to be called when an error occurs
 * @param onUploadingChange - Callback to be called when the uploading flag changes
 * @param autoUpload - should we auto upload the file if it's new
 */
export const useSelectResourceFileHandler = ({
  resourceType,
  onResult,
  onError,
  onUploadingChange,
  autoUpload,
}: {
  resourceType: ResourceContentType;
  onResult: ResultCallback;
  onError: ErrorCallback;
  onUploadingChange: UploadingChangeCallback;
  autoUpload?: boolean;
}): HookReturnCallback => {
  const customerActions = useCustomerActions();
  const notificationActions = useNotificationActions();
  const resourceActions = useResourceActions();

  // Maintain references to callbacks
  const refOnResult = useRef(onResult);
  refOnResult.current = onResult || noop;
  const refOnError = useRef(onError);
  refOnError.current = onError || noop;
  const refChangeUploadingFlag = useRef(onUploadingChange);
  refChangeUploadingFlag.current = onUploadingChange || noop;

  return useCallback(
    async (acceptedFiles, fileRejections) => {
      // Display error if we have any rejected file
      const rejectedFileErrorCode = fileRejections?.[0]?.errors?.[0]?.code;
      if (rejectedFileErrorCode) {
        const error = getResourceContentError(resourceType, rejectedFileErrorCode);
        notificationActions.displayNotification({ level: NOTIFICATION_LEVEL.error, ...error });
        return;
      }

      // Do not proceed if user did not select any file
      const acceptedFile = acceptedFiles?.[0];
      if (!acceptedFile) {
        return;
      }

      refChangeUploadingFlag.current(true);
      try {
        // Check if we already have a resource for the same file
        const checksum = await calculateFileMD5(acceptedFile);
        const existenceResult = await resourceActions.checkFileResourceExistence({
          type: resourceType,
          checksum,
        });

        // If we don't have related resource on BE, we can proceed with the file
        if (!existenceResult?.exist) {
          // in case we don't want to auto upload, just return the selected file ...
          if (!autoUpload) {
            refOnResult.current({ file: acceptedFile });
            return;
          }

          // ... otherwise, we should upload it as a new resource
          const enrichedFile = await enrichResourceFileInfo({ resourceType, file: acceptedFile });
          const extractedResource = await resourceActions.extractResource({
            name: enrichedFile.name,
            tags: [getContentTypeTag(resourceType)],
            file: enrichedFile,
            contentType: resourceType,
            checksum,
          });
          refOnResult.current({ resource: extractedResource, resourceId: extractedResource.id });

          customerActions.refreshCurrentCustomerFeatureLimit();
          return;
        }

        // Check if the resource is associated with the current workspace (loaded on FE)
        const resource = resourceActions.getWorkspaceResource(existenceResult.id);
        if (!resource) {
          const error = getResourceContentError(
            resourceType,
            RESOURCE_FILE_UPLOAD_ERROR_CODE.fileExistsButNotAssociatedToWorkspace
          );
          notificationActions.displayNotification({ level: NOTIFICATION_LEVEL.error, ...error });
          return;
        }

        // Proceed with existing resource id
        refOnResult.current({ resource, resourceId: resource.id });
      } catch (error) {
        const { title } = parseError(error as Error);
        refOnError.current(title);
      } finally {
        refChangeUploadingFlag.current(false);
      }
    },
    [resourceType, notificationActions, resourceActions, autoUpload, customerActions]
  );
};
